import {Component, ElementRef, Inject, ViewChild} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material';
import {Customer} from '../../model/Entities/Customer';
import {Product} from '../../model/Entities/Product';
import {FormControl, Validators} from '@angular/forms';
import {numberValidator} from '../../validators/number.validator';
import {intValidator} from '../../validators/int.validator';
import {Contract} from '../../model/Entities/Contract';
import {CustomerService} from '../../services/customer.service';
import {CustomerContactPersonService} from '../../services/customer-contact-person.service';
import {Filter} from '../../model/Helpers/Filter';
import {CustomerContactPerson} from '../../model/Entities/CustomerContactPerson';
import {MailService} from '../../services/mail.service';
import {Email} from '../../model/Entities/Email';
import {EmailAttachment} from '../../model/Entities/EmailAttachment';
import {SnackService} from '../../services/snack.service';
import {ConfigService} from '../../services/config.service';
import {UserService} from '../../services/user.service';
import {DocumentService} from '../../services/document.service';
import {Document} from '../../model/Entities/Document';

export interface AttachmentItem {
    name: string;
    type: 'invoice' | 'delivery_note' | 'custom';
    data?: string;
    removable: boolean;
    included: boolean;
}

@Component({
    selector: 'document-mail-dialog',
    templateUrl: './document-mail.dialog.component.html',
    styleUrls: ['./document-mail.dialog.component.scss']
})
export class DocumentMailDialogComponent {

    private document: Document;
    public contactPersons: CustomerContactPerson[] = [];

    receiver: CustomerContactPerson;
    otherReceiver: string;
    senderEmail = 'noreply@maxstorage.ms';
    subject: string;
    content: string;


    attachments: AttachmentItem[] = [];
    @ViewChild('customFileInput') customFileInput: ElementRef;
    // senderEmailFC = new FormControl({disabled: true}, [
    //     Validators.email,
    //     Validators.required
    // ]);

    subjectFC = new FormControl('', [
        Validators.required
    ]);

    get tinymceInit() {
        const base = this.CONFIG.get('TINYMCE_OPTIONS') || {};
        return { ...base, height: (base.height || 300) / 2 };
    }

    constructor(public dialog: MatDialogRef<DocumentMailDialogComponent>,
                private documentService: DocumentService,
                private customerContactPersonService: CustomerContactPersonService,
                private userService: UserService,
                private mailService: MailService,
                private snackService: SnackService,
                public CONFIG: ConfigService,
                @Inject(MAT_DIALOG_DATA) public data: any) {
        this.document = data.document;
        this.content = this.userService.getLoggedUser().emailSignature;
        if (this.userService.getLoggedUser().sendingEmail === '' || this.userService.getLoggedUser().sendingEmail == null)
            this.senderEmail = this.userService.getLoggedUser().email;
        else
            this.senderEmail = this.userService.getLoggedUser().sendingEmail;
        this.loadPersons();
        this.initAttachments();
    }

    loadPersons(): void {
        if (!this.document || !this.document.customer || !this.document.customer.id) {
            return;
        }
        this.customerContactPersonService
            .getSimpleList([new Filter('customer_id', this.document.customer.id)])
            .subscribe((persons: CustomerContactPerson[]) => {
                this.contactPersons = persons;
            });
    }

    initAttachments(): void {
        this.attachments.push({
            name: `doklad-${this.document.fullNumber}.pdf`,
            type: 'invoice',
            removable: false,
            included: true
        });

        if (this.document.contract && this.document.contract.id) {
            this.attachments.push({
                name: `kalkulace-${this.document.contract.id}.pdf`,
                type: 'delivery_note',
                removable: true,
                included: true
            });
        }
    }

    removeAttachment(index: number): void {
        const attachment = this.attachments[index];
        if (attachment && attachment.removable) {
            this.attachments.splice(index, 1);
        }
    }

    openCustomFileDialog(): void {
        if (this.customFileInput) {
            this.customFileInput.nativeElement.click();
        }
    }

    onCustomFileSelected(event: any): void {
        const files: FileList = event.target.files;
        if (!files || files.length === 0) {
            return;
        }

        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            this.readCustomFile(file);
        }
        event.target.value = '';
    }

    private readCustomFile(file: File): void {
        const fileReader: FileReader = new FileReader();

        fileReader.onloadend = () => {
            const base64Data = fileReader.result as string;
            this.attachments.push({
                name: file.name,
                type: 'custom',
                data: base64Data,
                removable: true,
                included: true
            });
        };

        fileReader.readAsDataURL(file);
    }

    getIncludedAttachments(): AttachmentItem[] {
        return this.attachments.filter(a => a.included);
    }

    checkValidity(): boolean {
        return this.subjectFC.valid && this.content != null;
    }

    submit(): void {
        const message: Email = new Email();
        message.subject = this.subject;
        message.message = this.content;

        let recipients: string[] = [];
        if (this.receiver && this.receiver.email) {
            recipients.push(this.receiver.email);
        }
        if (this.otherReceiver && this.otherReceiver.trim()) {
            recipients.push(this.otherReceiver.trim());
        }
        message.recipient = recipients.join(',');

        message.sender = this.senderEmail;
        message.documents = [this.document.id];

        const deliveryNoteAttachment = this.attachments.find(a => a.type === 'delivery_note' && a.included);
        if (deliveryNoteAttachment && this.document.contract && this.document.contract.id) {
            message.contracts.push(this.document.contract.id); 
        }
        const customAttachments = this.attachments.filter(a => a.type === 'custom' && a.included && a.data);
        for (const attachment of customAttachments) {
            const emailAttachment = new EmailAttachment();
            emailAttachment.name = attachment.name;
            emailAttachment.data = attachment.data;
            message.attachments.push(emailAttachment);
        }

        this.mailService.sendEmail(message).subscribe((e: Email) => {
            this.snackService.showSuccess(`Email "${e.subject}" byl odeslán na adresu ${e.recipient}`);
        });

        this.dialog.close(true);
    }


    private formatDate(date: Date): string {
        if (!date) {
            return '';
        }
        const d = new Date(date);
        return d.toLocaleDateString('cs-CZ');
    }

    loadTemplate(number: number) {
        switch (number) {
            case 1:
                this.subject = 'Nová faktura č. ' + this.document.fullNumber;
                this.content = `
                <p>Dobrý den,</p>
                <p>zasíláme Vám novou fakturu č. <strong>${this.document.fullNumber}</strong>
                se splatností do <strong>${this.formatDate(this.document.dueDate)}</strong>.</p>
                <p>V případě dotazů nás neváhejte kontaktovat.</p>
                <p>S pozdravem<br></p>
            `;
                break;

            case 2:
                this.subject = 'První upomínka platby faktury č. ' + this.document.fullNumber;
                this.content = `
                <p>Dobrý den,</p>
                <p>evidujeme, že faktura č. <strong>${this.document.fullNumber}</strong>
                po splatnosti dosud nebyla uhrazena.</p>
                <p>Prosíme o úhradu v nejbližším možném termínu.</p>
                <p>Děkujeme.<br>S pozdravem<br></p>
            `;
                break;

            case 3:
                this.subject = 'Druhá upomínka platby faktury č. ' + this.document.fullNumber;
                this.content = `
                <p>Dobrý den,</p>
                <p>faktura č. <strong>${this.document.fullNumber}</strong>
                je stále po splatnosti.</p>
                <p>Žádáme o její úhradu bez zbytečného odkladu. Pokud již byla zaplacena, prosíme o informaci.</p>
                <p>S pozdravem<br></p>
            `;
                break;

            case 4:
                this.subject = 'Třetí upomínka platby faktury č. ' + this.document.fullNumber;
                this.content = `
                <p>Dobrý den,</p>
                <p>ani po opakovaných výzvách nebyla uhrazena faktura č. <strong>${this.document.fullNumber}</strong>.</p>
                <p>Pokud platba neproběhne neprodleně, budeme nuceni předat případ k dalšímu řešení.</p>
                <p>Pokud jste mezitím uhradili, prosíme o potvrzení.</p>
                <p>S pozdravem<br></p>
            `;
                break;
        }

        const signature = this.userService.getLoggedUser().emailSignature;
        if (signature) {
            this.content += signature;
        }
    }

}



// WEBPACK FOOTER //
// /app/src/DocumentsModule/DocumentMailDialogComponent/document-mail.dialog.component.ts